import { StanCalendar, StanDropdown, StanInput } from '@/components';
import { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { StanFloatingMenu } from '@/components/core-components/StanDropdown/FloatingMenu/StanFloatingMenu';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { TIME_OPTIONS } from '@/lib/utils/timeUtils';

interface DateTimePickerProps {
    selectedDate?: Date;
    selectedTime?: number;
    onDateChange: (date: Date | undefined) => void;
    onTimeChange: (hour: number) => void;
    minDate?: Date;
    error?: boolean;
    errorMessage?: string;
    hintText?: string;
    disabled?: boolean;
}

export const DateTimePicker = ({
    selectedDate,
    selectedTime = 0,
    onDateChange,
    onTimeChange,
    minDate,
    error = false,
    errorMessage,
    hintText,
    disabled = false,
}: DateTimePickerProps) => {
    // Get current time value for dropdown
    const currentTimeValue = useMemo(() => {
        const option = TIME_OPTIONS.find(opt => opt.value === String(selectedTime));
        return option || TIME_OPTIONS[0];
    }, [selectedTime]);

    const handleTimeSelect = (value: DropdownValue) => {
        onTimeChange(Number(value.value));
    };

    // Format the selected date for display
    const formattedDate = selectedDate ? format(selectedDate, 'MMM dd, yyyy') : 'Select date';

    return (
        <div className=''>
            <div className='date-time-picker flex gap-2 max-md:flex-col md:gap-4'>
                {/* Date Picker */}
                <div className='min-w-0 flex-1'>
                    {disabled ? (
                        <div className='relative'>
                            <FiChevronDown className='text-neutral-fg-disabled absolute top-1/2 right-3 -translate-y-1/2' />
                            <StanInput
                                id='date-picker'
                                name='date-picker'
                                innerLabel='Date'
                                value={formattedDate}
                                onChange={() => {}}
                                readOnly
                                error={error}
                                hintText={hintText}
                                disabled={disabled}
                            />
                        </div>
                    ) : (
                        <StanFloatingMenu
                            name='date-picker-menu'
                            placement='bottom-start'
                            className='md:min-h-calendar-height! overflow-y-auto'
                            trigger={
                                <div>
                                    <label htmlFor='date' className='text-label-xs-bold'>
                                        Date
                                    </label>
                                    <div className='relative cursor-pointer'>
                                        <FiChevronDown className='text-neutral-fg-disabled absolute top-1/2 right-3 -translate-y-1/2' />
                                        <StanInput
                                            id='date-picker'
                                            name='date-picker'
                                            value={formattedDate}
                                            onChange={() => {}}
                                            error={error}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            }
                        >
                            {({ closeMenu }) => (
                                <div className='w-auto'>
                                    <StanCalendar
                                        analyticsName='flow-date-picker'
                                        mode='single'
                                        selected={selectedDate}
                                        defaultMonth={selectedDate}
                                        onSelect={date => {
                                            onDateChange(date);
                                            closeMenu();
                                        }}
                                        disabled={minDate ? { before: minDate } : undefined}
                                    />
                                </div>
                            )}
                        </StanFloatingMenu>
                    )}
                </div>

                {/* Time Picker */}
                <div className='min-w-0 flex-1'>
                    <label htmlFor='time-picker' className='text-label-xs-bold'>
                        Time
                    </label>
                    <StanDropdown
                        id='time-picker'
                        name='time-picker'
                        options={TIME_OPTIONS}
                        currentValue={currentTimeValue}
                        onSelect={handleTimeSelect}
                        error={error}
                        preventDeselect
                    />
                </div>
            </div>

            {/* Error message */}
            {error && errorMessage && (
                <p className='text-negative-active text-label-xs-bold mt-1 text-left'>{errorMessage}</p>
            )}

            {/* Hint text when no error */}
            {!error && hintText && <p className='text-gray text-label-xs-regular mt-1 text-left'>{hintText}</p>}
        </div>
    );
};
